/**
 * Document List Component
 * Displays uploaded documents with view and delete functionality
 */
import { useEffect, useState } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { DocumentViewer } from './DocumentViewer';
import type { Document } from '../../types';

export function DocumentList() {
  const { documents, isLoading, fetchDocuments, deleteDocument } = useDocumentStore();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    setDeletingId(documentId);
    try {
      await deleteDocument(documentId);
    } catch (error) {
      console.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading && documents.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Uploaded Documents</h2>
          <span className="text-sm text-gray-400">{documents.length} documents</span>
        </div>

        {documents.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-400">No documents uploaded yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Upload documents to get started with RAG
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onView={setViewingDocument}
                onDelete={handleDelete}
                isDeleting={deletingId === doc.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewer document={viewingDocument} onClose={() => setViewingDocument(null)} />
    </>
  );
}

interface DocumentCardProps {
  document: Document;
  onView: (document: Document) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function DocumentCard({ document, onView, onDelete, isDeleting }: DocumentCardProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return 'text-red-500';
    } else if (mimeType.includes('wordprocessingml')) {
      return 'text-blue-500';
    } else if (mimeType.includes('text')) {
      return 'text-green-500';
    }
    return 'text-gray-500';
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Document Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {/* File Icon */}
            <svg
              className={`w-5 h-5 ${getFileIcon(document.mimeType)} flex-shrink-0`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
            <h3 className="font-medium text-white truncate">{document.name}</h3>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>{formatFileSize(document.size)}</span>
            <span>•</span>
            <span>{document.chunkCount} chunks</span>
            <span>•</span>
            <span>{formatDate(document.createdAt)}</span>
          </div>

          {/* Metadata Preview */}
          {document.metadata && (
            <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
              <span>{document.metadata.wordCount.toLocaleString()} words</span>
              <span>•</span>
              <span>{document.metadata.language.toUpperCase()}</span>
              <span>•</span>
              <span>{document.metadata.chunkingStrategy}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* View Button */}
          <button
            onClick={() => onView(document)}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-purple-500 hover:bg-purple-500/10 rounded-lg transition-colors"
            title="View details"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </button>

          {/* Delete Button */}
          <button
            onClick={() => onDelete(document.id)}
            disabled={isDeleting}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
            title="Delete document"
          >
            {isDeleting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
